import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { setTenantSlugForApi } from '../utils/tenantApiHeaders';
import {
  getStoredTenantSlug,
  setStoredTenantSlug,
} from '../utils/tenantStorage';

export interface TenantContextValue {
  /** Subdomain label, e.g. `acme` for acme.sliderule.com */
  tenantSlug: string;
  /** Human label for the header */
  displayLabel: string;
  /** True when using ?tenant=, VITE override, or saved slug on a non-subdomain host */
  isDevOverride: boolean;
  /** Persist slug and sync API headers (cookie + localStorage on generic / non-subdomain hosts). */
  setTenantSlug: (slug: string) => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

function hasProductionTenantSubdomain(): boolean {
  const host = window.location?.hostname ?? '';
  if (!host) {
    return false;
  }
  const parts = host.split('.');
  return (
    parts.length >= 3 && host.endsWith('sliderule.com') && parts[0] !== 'www'
  );
}

/** e.g. ``engenuitylab.localhost`` — first label is tenant (browsers resolve to 127.0.0.1). */
function hasLocalhostTenantSubdomain(): boolean {
  const host = window.location?.hostname ?? '';
  if (!host) {
    return false;
  }
  const parts = host.split('.');
  return (
    parts.length >= 2 &&
    parts[parts.length - 1] === 'localhost' &&
    parts[0] !== 'www' &&
    parts[0] !== 'localhost'
  );
}

/** Tenant is implied by hostname; slug must not be overridden from cookie/query. */
export function isTenantFixedByHostname(): boolean {
  return hasProductionTenantSubdomain() || hasLocalhostTenantSubdomain();
}

export function isLocalDevHost(): boolean {
  const h = window.location?.hostname ?? '';
  return h === 'localhost' || h === '127.0.0.1';
}

export function resolveTenantSlug(): string {
  const host = window.location?.hostname ?? '';
  if (!host) {
    return '';
  }
  const parts = host.split('.');
  if (hasProductionTenantSubdomain()) {
    return parts[0].toLowerCase();
  }
  if (hasLocalhostTenantSubdomain()) {
    return parts[0].toLowerCase();
  }
  // Generic hosts (localhost, *.replit.app, preview URLs, etc.): ?tenant=, build-time env,
  // then cookie/localStorage from a prior visit or TenantPicker.
  const q = new URLSearchParams(window.location.search).get('tenant');
  if (q?.trim()) {
    return q.trim().toLowerCase();
  }
  const fromEnv = import.meta.env.VITE_TENANT_SLUG;
  if (typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv.trim().toLowerCase();
  }
  const stored = getStoredTenantSlug();
  if (stored) {
    return stored;
  }
  return '';
}

/** True when tenant came from query/env/storage rather than hostname subdomain. */
function computeIsDevOverride(_slug: string): boolean {
  if (isTenantFixedByHostname()) {
    return false;
  }
  if (new URLSearchParams(window.location.search).has('tenant')) {
    return true;
  }
  if (
    typeof import.meta.env.VITE_TENANT_SLUG === 'string' &&
    import.meta.env.VITE_TENANT_SLUG.trim() !== ''
  ) {
    return true;
  }
  if (getStoredTenantSlug()) {
    return true;
  }
  return false;
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [tenantSlug, setTenantSlugState] = useState<string>(() =>
    resolveTenantSlug()
  );

  useEffect(() => {
    setTenantSlugState(resolveTenantSlug());
  }, [location.search, location.pathname]);

  const setTenantSlug = useCallback((raw: string) => {
    const s = raw.trim().toLowerCase();
    const next = s;
    setTenantSlugState(next);
    if (!isTenantFixedByHostname()) {
      if (next === '') {
        setStoredTenantSlug(null);
      } else {
        setStoredTenantSlug(next);
      }
    }
  }, []);

  const isDevOverride = useMemo(
    () => computeIsDevOverride(tenantSlug),
    [tenantSlug]
  );

  const value = useMemo((): TenantContextValue => {
    const displayLabel =
      tenantSlug === '' ? 'Slide Rule' : tenantSlug.replace(/-/g, ' ');
    return {
      tenantSlug,
      displayLabel,
      isDevOverride,
      setTenantSlug,
    };
  }, [tenantSlug, isDevOverride, setTenantSlug]);

  useEffect(() => {
    setTenantSlugForApi(value.tenantSlug === '' ? null : value.tenantSlug);
    return () => setTenantSlugForApi(null);
  }, [value.tenantSlug]);

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return ctx;
}
